// Reusable schedule editor for campaign create + edit. Replaces the
// raw JSON textarea that previously let operators silently save bad
// `schedule_json` (root cause of the May 2026 ProdTest 1
// "next opens at 04:00 UTC" bug — see docs/outreach-backlog.md B2).
//
// The schedule shape stays compatible with the backend's
// `send_window.parse_schedule`: same JSON keys, same semantics.
// Timezone is locked to America/New_York for v1 — Brad's preference;
// flip to a select if other zones become a real need.

import styles from '../pages/OutreachPage.module.css';

export interface ScheduleValue {
  weekdays: number[];     // ISO 1=Mon..7=Sun
  start_hour: number;     // 0..23, inclusive
  // 1..24, EXCLUSIVE — sends stop strictly before this hour. 24 means
  // "midnight of the next day", i.e. the full day's last hour
  // (23:00–23:59) is sendable. Without that, end_hour=23 stops sends
  // at 22:59 and there is no way to fire between 11pm and midnight.
  end_hour: number;
  timezone: string;       // IANA, e.g. 'America/New_York'
}

export const DEFAULT_SCHEDULE: ScheduleValue = {
  weekdays: [1, 2, 3, 4, 5],
  start_hour: 9,
  end_hour: 17,
  timezone: 'America/New_York',
};


export function parseSchedule(json: string | null | undefined): ScheduleValue {
  if (!json) return { ...DEFAULT_SCHEDULE };
  let parsed: Partial<ScheduleValue> = {};
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ...DEFAULT_SCHEDULE };
  }
  const weekdays = Array.isArray(parsed.weekdays)
    ? parsed.weekdays
        .map((d) => Number(d))
        .filter((d) => Number.isInteger(d) && d >= 1 && d <= 7)
    : DEFAULT_SCHEDULE.weekdays;
  const sh = parsed.start_hour;
  const eh = parsed.end_hour;
  const start_hour =
    typeof sh === 'number' && sh >= 0 && sh <= 23 ? sh : DEFAULT_SCHEDULE.start_hour;
  // end_hour spans 0..24 inclusive — 24 = midnight (end of day).
  const end_hour =
    typeof eh === 'number' && eh >= 0 && eh <= 24 ? eh : DEFAULT_SCHEDULE.end_hour;
  const timezone =
    typeof parsed.timezone === 'string' && parsed.timezone
      ? parsed.timezone
      : DEFAULT_SCHEDULE.timezone;
  return { weekdays, start_hour, end_hour, timezone };
}


export function scheduleToJson(value: ScheduleValue): string {
  return JSON.stringify(value);
}


/**
 * Validates a ScheduleValue. Returns null when valid, or an error
 * string when not. Caller surfaces the message via the form's error
 * banner; we never throw from here.
 */
export function validateSchedule(value: ScheduleValue): string | null {
  if (value.weekdays.length === 0) {
    return 'Pick at least one weekday.';
  }
  if (value.start_hour < 0 || value.start_hour > 23) {
    return 'Start hour must be between 0 and 23.';
  }
  // End is exclusive; 24 represents midnight (end of day) so the
  // 23:00–23:59 hour is sendable.
  if (value.end_hour < 1 || value.end_hour > 24) {
    return 'End hour must be between 1 and 24.';
  }
  if (value.start_hour >= value.end_hour) {
    return (
      'Start hour must be earlier than End hour. ' +
      '(End is exclusive — set End to one hour after the last sendable hour.)'
    );
  }
  return null;
}


// Mon=1..Sun=7 in ISO. Display order matches the typical work-week
// (Mon–Fri grouped, Sat–Sun trailing) so weekend toggling reads naturally.
const WEEKDAYS: { iso: number; label: string }[] = [
  { iso: 1, label: 'Mon' },
  { iso: 2, label: 'Tue' },
  { iso: 3, label: 'Wed' },
  { iso: 4, label: 'Thu' },
  { iso: 5, label: 'Fri' },
  { iso: 6, label: 'Sat' },
  { iso: 7, label: 'Sun' },
];


function hourLabel(h: number): string {
  // 12-hour with AM/PM suffix — matches how operators read clocks.
  if (h === 0) return '12 AM (midnight)';
  if (h === 12) return '12 PM (noon)';
  // h=24 is the exclusive end-of-day marker. Distinct phrasing from
  // h=0 so an operator scanning the dropdown sees the difference
  // between "starts at midnight" and "runs through midnight".
  if (h === 24) return '12 AM (end of day)';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}


export function ScheduleFields({
  value,
  onChange,
}: {
  value: ScheduleValue;
  onChange: (next: ScheduleValue) => void;
}) {
  function toggleWeekday(iso: number) {
    const set = new Set(value.weekdays);
    if (set.has(iso)) set.delete(iso);
    else set.add(iso);
    onChange({ ...value, weekdays: Array.from(set).sort((a, b) => a - b) });
  }

  return (
    <div>
      <div className={styles.label} style={{ marginBottom: 6 }}>
        Days
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {WEEKDAYS.map((d) => {
          const selected = value.weekdays.includes(d.iso);
          return (
            <button
              key={d.iso}
              type="button"
              className={`${styles.button} ${selected ? styles.buttonPrimary : ''}`}
              onClick={() => toggleWeekday(d.iso)}
              style={{ minWidth: 52, fontSize: 12 }}
            >
              {selected ? '✓ ' : ''}
              {d.label}
            </button>
          );
        })}
      </div>

      <div className={styles.formGrid}>
        <label className={styles.label}>Start hour</label>
        <select
          className={styles.input}
          value={value.start_hour}
          onChange={(e) =>
            onChange({ ...value, start_hour: Number(e.target.value) })
          }
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>
              {hourLabel(h)}
            </option>
          ))}
        </select>

        <label className={styles.label}>End hour (exclusive)</label>
        <select
          className={styles.input}
          value={value.end_hour}
          onChange={(e) =>
            onChange({ ...value, end_hour: Number(e.target.value) })
          }
        >
          {/* 1..24: end is exclusive, so the smallest meaningful end
              is 1AM (window of 12AM–1AM), and 24 = end of day so 11PM
              hour is sendable. Skipping 0 because end_hour=0 would
              never satisfy start_hour < end_hour. */}
          {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>
              {hourLabel(h)}
            </option>
          ))}
        </select>

        <label className={styles.label}>Timezone</label>
        <input
          className={styles.input}
          type="text"
          value="America/New_York (EST / EDT)"
          disabled
          title="Timezone is locked to America/New_York for v1."
        />
      </div>

      <div className={styles.helpText} style={{ marginTop: 6 }}>
        Sends fire from <strong>{hourLabel(value.start_hour)}</strong> up to (but
        not including) <strong>{hourLabel(value.end_hour)}</strong> on the picked
        days, in America/New_York. Daylight saving is handled automatically.
      </div>
    </div>
  );
}
